import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { openPdf } from '../utils/pdf';
import { useToast } from '../components/Toast';
import { BeoordelingModal, type BeoordeelActie } from '../components/BeoordelingModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const fmt = (n: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);

// Statuslabel per soort (zelfde enum, andere betekenis)
function statusInfo(soort: string, status: string): { label: string; cls: string } {
  if (soort === 'incasso') {
    const m: Record<string, { label: string; cls: string }> = {
      submitted: { label: 'Overgedragen', cls: 'bg-mfb-blue/10 text-mfb-blue' },
      under_review: { label: 'In behandeling', cls: 'bg-amber-100 text-amber-700' },
      paid: { label: 'Geslaagd', cls: 'bg-emerald-100 text-emerald-700' },
      denied: { label: 'Mislukt', cls: 'bg-red-100 text-red-700' },
      withdrawn: { label: 'Ingetrokken', cls: 'bg-slate-100 text-slate-500' },
    };
    return m[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  }
  const m: Record<string, { label: string; cls: string }> = {
    submitted: { label: 'Ingediend', cls: 'bg-mfb-blue/10 text-mfb-blue' },
    under_review: { label: 'In beoordeling', cls: 'bg-amber-100 text-amber-700' },
    approved: { label: 'Goedgekeurd', cls: 'bg-emerald-100 text-emerald-700' },
    paid: { label: 'Uitgekeerd', cls: 'bg-emerald-200 text-emerald-800' },
    denied: { label: 'Afgewezen', cls: 'bg-red-100 text-red-700' },
    disputed: { label: 'Betwist', cls: 'bg-orange-100 text-orange-700' },
  };
  return m[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
}

const OPEN_STATES = ['submitted', 'under_review', 'disputed'];

export const AdminClaimsPage: React.FC<{ vasteSoort?: 'incasso' | 'schade' }> = ({ vasteSoort }) => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };

  const [claims, setClaims] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [soortFilter, setSoortFilter] = useState<'alle' | 'incasso' | 'schade'>(vasteSoort ?? 'alle');
  const [statusFilter, setStatusFilter] = useState<'alle' | 'open' | 'afgewikkeld' | 'uitgekeerd'>('alle');
  const [toonArchief, setToonArchief] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [redenen, setRedenen] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');
  const [beoordeel, setBeoordeel] = useState<any>(null);   // schadedossier dat beoordeeld wordt
  const [goedkeur, setGoedkeur] = useState<{ claim: any; bedrag: number } | null>(null);   // positieve schadebeoordeling
  const notify = useToast();

  const schadeActies = (c: any): BeoordeelActie[] => [
    {
      key: 'deny', label: 'Afwijzen (geen uitkering)', kleur: 'red',
      redenen: ['Niet verzekerd', 'Buiten dekking', 'Te laat gemeld', 'Onvoldoende onderbouwing', 'Anders'],
      template_key: 'schade_afgewezen', bijlage: 'Afwijzingsbrief (PDF)',
      context: (red, toel) => ({ claim_number: c.claim_number, debiteur: c.debiteur, reden: red, toelichting: toel }),
    },
    {
      key: 'betwisten', label: 'Betwisten (voorlopig niet uitkeren)', kleur: 'amber',
      redenen: ['Debiteur betwist de vordering', 'Debiteur betwist de levering', 'Aanvullende stukken nodig', 'In onderzoek', 'Anders'],
      template_key: 'schade_betwist', bijlage: 'Betwistingsbrief (PDF)',
      context: (red, toel) => ({ claim_number: c.claim_number, debiteur: c.debiteur, reden: red, toelichting: toel }),
    },
  ];

  const goedkeurActies = (c: any, bedrag: number): BeoordeelActie[] => [
    {
      key: 'goedkeuren', label: 'Goedkeuren & uitkeren', kleur: 'green',
      template_key: 'schade_goedgekeurd', bijlage: 'Schadestaat (PDF)',
      context: (_red, _toel) => ({ claim_number: c.claim_number, bedrag: fmt(bedrag) }),
    },
  ];

  const voerGoedkeurUit = async (claimId: string, bedrag: number, onderwerp: string, bericht: string) => {
    const r = await fetch(`${API_BASE_URL}/api/admin/claims/${claimId}/approve`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ approved_amount: bedrag, onderwerp: onderwerp || null, bericht: bericht || null }),
    });
    if (!r.ok) { const e = await r.json().catch(() => ({})); notify.error(e.detail || 'Actie mislukt'); return null; }
    notify.success(`Schade goedgekeurd — uitkering ${fmt(bedrag)} geboekt, bericht verzonden.`);
    load();
    return r.json();
  };

  const voerSchadeUit = async (claimId: string, actieKey: string, reden: string, toelichting: string, onderwerp: string, bericht: string) => {
    const path = actieKey === 'betwisten' ? 'betwisten' : 'deny';
    const r = await fetch(`${API_BASE_URL}/api/admin/claims/${claimId}/${path}`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reden, toelichting, onderwerp: onderwerp || null, bericht: bericht || null }),
    });
    if (!r.ok) { const e = await r.json().catch(() => ({})); notify.error(e.detail || 'Actie mislukt'); return null; }
    notify.success(actieKey === 'betwisten' ? 'Dossier betwist — bericht verzonden.' : 'Schade afgewezen — bericht verzonden.');
    load();
    return r.json();
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/claims`, { headers, cache: 'no-store' });
      setClaims(r.ok ? await r.json() : []);
    } catch { notify.error('Dossiers laden mislukt'); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const act = async (id: string, path: string, body?: any) => {
    setBusy(id);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/claims/${id}/${path}`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Actie mislukt'); }
      notify.success('Dossier bijgewerkt — het lid is genotificeerd.');
      await load();
    } catch (e) { notify.error(e instanceof Error ? e.message : 'Actie mislukt'); } finally { setBusy(''); }
  };

  const archiveer = async (id: string, gearchiveerd: boolean) => {
    setBusy(id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/archiveren/dossier/${id}`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ gearchiveerd }),
      });
      if (!res.ok) throw new Error();
      notify.success(gearchiveerd ? 'Dossier naar archief verplaatst.' : 'Teruggehaald uit archief.');
      await load();
    } catch { notify.error('Archiveren mislukt.'); } finally { setBusy(''); }
  };

  const soortOf = (c: any) => (c.soort || 'schade');
  const filtered = claims.filter((c) => {
    if (soortFilter !== 'alle' && soortOf(c) !== soortFilter) return false;
    const open = OPEN_STATES.includes(c.status);
    if (statusFilter === 'open') return open && !c.gearchiveerd;
    if (statusFilter === 'afgewikkeld') return !open;
    if (statusFilter === 'uitgekeerd') return c.status === 'paid';
    return toonArchief ? true : (open && !c.gearchiveerd);   // 'alle'
  });
  // Widget-klik zet soort + status-weergave ineen
  const kies = (soort: 'alle' | 'incasso' | 'schade', status: 'alle' | 'open' | 'afgewikkeld' | 'uitgekeerd') => {
    if (!vasteSoort) setSoortFilter(soort);
    setStatusFilter(status);
    setToonArchief(status === 'afgewikkeld' || status === 'uitgekeerd');
  };
  const actief = (soort: 'alle' | 'incasso' | 'schade', status: string) =>
    (vasteSoort || soortFilter === soort) && statusFilter === status;

  // Overzichtstellers
  const tel = (soort: string, pred: (c: any) => boolean) => claims.filter((c) => soortOf(c) === soort && pred(c)).length;
  const incassoOpen = tel('incasso', (c) => OPEN_STATES.includes(c.status));
  const incassoKlaar = tel('incasso', (c) => !OPEN_STATES.includes(c.status));
  const schadeOpen = tel('schade', (c) => OPEN_STATES.includes(c.status));
  const schadeUit = tel('schade', (c) => c.status === 'paid');
  const teBeoordelen = claims.filter((c) => OPEN_STATES.includes(c.status)).length;

  return (
    <Layout>
      <div className="mb-6">
        <p className="text-sm text-slate-500">
          {vasteSoort === 'incasso' ? (
            <>Overgedragen <strong className="text-slate-700">incassodossiers</strong> — overdracht en doorzetten. Een mislukte incasso kan escaleren naar een schadedossier.</>
          ) : vasteSoort === 'schade' ? (
            <>Ingediende <strong className="text-slate-700">schadedossiers</strong> — beoordeling van het verzekerde karakter en uitkering.</>
          ) : (
            <>Overgedragen dossiers van leden. <strong className="text-slate-700">Incasso</strong> = overdracht/doorzetten, <strong className="text-slate-700"> schade</strong> = financiële afhandeling (uitkering). Een mislukte incasso kan escaleren naar schade.</>
          )}
        </p>
      </div>

      {/* Overzicht */}
      <div className={`grid grid-cols-2 gap-3 mb-6 ${vasteSoort ? 'lg:grid-cols-3' : 'lg:grid-cols-5'}`}>
        <OverviewCard label="Te beoordelen" value={teBeoordelen} tone="amber"
          onClick={() => kies(vasteSoort ?? 'alle', 'open')} active={actief(vasteSoort ?? 'alle', 'open')} />
        {vasteSoort !== 'schade' && <OverviewCard label="Incasso open" value={incassoOpen} tone="blue"
          onClick={() => kies('incasso', 'open')} active={actief('incasso', 'open')} />}
        {vasteSoort !== 'schade' && <OverviewCard label="Incasso afgewikkeld" value={incassoKlaar} tone="slate"
          onClick={() => kies('incasso', 'afgewikkeld')} active={actief('incasso', 'afgewikkeld')} />}
        {vasteSoort !== 'incasso' && <OverviewCard label="Schade open" value={schadeOpen} tone="blue"
          onClick={() => kies('schade', 'open')} active={actief('schade', 'open')} />}
        {vasteSoort !== 'incasso' && <OverviewCard label="Schade uitgekeerd" value={schadeUit} tone="emerald"
          onClick={() => kies('schade', 'uitgekeerd')} active={actief('schade', 'uitgekeerd')} />}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {!vasteSoort && (['alle', 'incasso', 'schade'] as const).map((k) => (
          <button key={k} onClick={() => { setSoortFilter(k); setStatusFilter('alle'); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize border ${
              soortFilter === k && statusFilter === 'alle' ? 'bg-mfb-blue text-white border-mfb-blue' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
            }`}>{k}</button>
        ))}
        <label className="ml-2 inline-flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={toonArchief} onChange={(e) => setToonArchief(e.target.checked)} /> Toon archief (afgehandeld + gearchiveerd)
        </label>
        <span className="ml-auto text-sm text-slate-500">{filtered.length} dossier(s)</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">Geen dossiers in deze selectie.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50"><tr>
              {['Dossier', 'Soort', 'Lid / debiteur', 'Bedrag', 'Status', ''].map((h) => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((c) => {
                const soort = soortOf(c);
                const st = statusInfo(soort, c.status);
                const isOpen = expanded === c.id;
                return (
                  <React.Fragment key={c.id}>
                    <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(isOpen ? null : c.id)}>
                      <td className="px-5 py-3">
                        <div className="font-medium text-mfb-blue">{c.claim_number}</div>
                        <div className="text-xs text-slate-400">{c.collectiviteit} · factuur {c.factuur || '—'} · {c.datum}</div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${soort === 'incasso' ? 'bg-mfb-blue/10 text-mfb-blue' : 'bg-mfb-orange/10 text-mfb-orange'}`}>
                          {soort === 'incasso' ? 'Incasso' : 'Schade'}
                        </span>
                        {c.parent_dossier_id && <div className="text-[10px] text-slate-400 mt-0.5">geëscaleerd</div>}
                      </td>
                      <td className="px-5 py-3 text-slate-700">{c.lid}<div className="text-xs text-slate-400">{c.debiteur}</div></td>
                      <td className="px-5 py-3 text-slate-700">
                        {fmt(c.claim_amount)}
                        {c.approved_amount != null && <div className="text-xs text-emerald-600">uitkering: {fmt(c.approved_amount)}</div>}
                      </td>
                      <td className="px-5 py-3"><span className={`px-3 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>{c.gearchiveerd && <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 text-slate-600">archief</span>}</td>
                      <td className="px-5 py-3 text-right text-xs font-medium text-mfb-blue">{isOpen ? '▲ Sluiten' : '▼ Behandelen'}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/60"><td colSpan={6} className="px-5 py-4">
                        <HandlingPanel
                          c={c} soort={soort} busy={busy === c.id}
                          amount={amounts[c.id] ?? String(c.gedekt_bedrag || c.claim_amount || 0)}
                          setAmount={(v) => setAmounts({ ...amounts, [c.id]: v })}
                          reden={redenen[c.id] ?? ''}
                          setReden={(v) => setRedenen({ ...redenen, [c.id]: v })}
                          act={act}
                          token={accessToken!}
                          onBeoordeel={(claim: any) => setBeoordeel(claim)}
                          onGoedkeuren={(claim: any, bedrag: number) => setGoedkeur({ claim, bedrag })}
                          archiveer={archiveer}
                          schadestaat={() => openPdf(`/api/admin/claims/${c.id}/schadestaat`, accessToken!)}
                        />
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}

      {beoordeel && (
        <BeoordelingModal
          titel="Schadedossier beoordelen"
          context={`Dossier ${beoordeel.claim_number} · debiteur ${beoordeel.debiteur} · gedekt ${fmt(beoordeel.gedekt_bedrag)}`}
          acties={schadeActies(beoordeel)} token={accessToken!} invoiceId={beoordeel.invoice_id}
          uitvoeren={(actieKey, reden, toel, ond, ber) => voerSchadeUit(beoordeel.id, actieKey, reden, toel, ond, ber)}
          onClose={() => setBeoordeel(null)} onDone={() => load()}
        />
      )}

      {goedkeur && (
        <BeoordelingModal
          titel="Schade goedkeuren & uitkeren"
          context={`Dossier ${goedkeur.claim.claim_number} · debiteur ${goedkeur.claim.debiteur} · uitkering ${fmt(goedkeur.bedrag)}`}
          acties={goedkeurActies(goedkeur.claim, goedkeur.bedrag)} token={accessToken!} invoiceId={goedkeur.claim.invoice_id}
          uitvoeren={(_actieKey, _reden, _toel, ond, ber) => voerGoedkeurUit(goedkeur.claim.id, goedkeur.bedrag, ond, ber)}
          onClose={() => setGoedkeur(null)} onDone={() => { setGoedkeur(null); load(); }}
        />
      )}
    </Layout>
  );
};

function OverviewCard({ label, value, tone, onClick, active }: { label: string; value: number; tone: 'amber' | 'blue' | 'slate' | 'emerald'; onClick?: () => void; active?: boolean }) {
  const map: Record<string, string> = {
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    blue: 'text-mfb-blue bg-mfb-blue/5 border-mfb-blue/20',
    slate: 'text-slate-600 bg-slate-50 border-slate-200',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  };
  return (
    <button type="button" onClick={onClick}
      className={`text-left rounded-xl border p-4 transition ${map[tone]} ${onClick ? 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer' : ''} ${active ? 'ring-2 ring-mfb-blue' : ''}`}>
      <div className="text-2xl font-display font-bold tabular-nums">{value}</div>
      <div className="text-xs font-medium mt-0.5">{label}</div>
    </button>
  );
}

function Check({ ok, label }: { ok?: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${ok ? 'text-emerald-700' : 'text-slate-400'}`}>
      <span>{ok ? '✓' : '○'}</span>{label}
    </span>
  );
}

function AttachmentsView({ invoiceId, token }: { invoiceId: string; token: string }) {
  const [rows, setRows] = useState<any[]>([]);
  const [uploaden, setUploaden] = useState(false);
  const laad = useCallback(() => {
    if (!invoiceId) return;
    fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/bijlagen`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : [])).then(setRows).catch(() => setRows([]));
  }, [invoiceId, token]);
  useEffect(() => { laad(); }, [laad]);
  const open = (id: string) => openPdf(`/api/admin/bijlagen/${id}`, token);
  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploaden(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData(); fd.append('file', file);
        await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/beoordeling-bijlage`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
        });
      }
      laad();
    } finally { setUploaden(false); }
  };
  return (
    <div className="text-xs flex flex-wrap items-center gap-2">
      <span className="text-slate-400">Bijlagen:</span>
      {rows.length === 0 ? <span className="text-slate-400">geen</span> : rows.map((a) => (
        <button key={a.id} onClick={() => open(a.id)} className="text-mfb-blue hover:underline">📎 {a.bestandsnaam}</button>
      ))}
      <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-slate-600 hover:bg-white">
        <input type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
        {uploaden ? 'uploaden…' : '+ bijlage'}
      </label>
    </div>
  );
}

function HandlingPanel({ c, soort, busy, amount, setAmount, reden, setReden, act, schadestaat, token, onBeoordeel, onGoedkeuren, archiveer }: {
  c: any; soort: string; busy: boolean;
  amount: string; setAmount: (v: string) => void; reden: string; setReden: (v: string) => void;
  act: (id: string, path: string, body?: any) => void; schadestaat: () => void; token: string;
  onBeoordeel: (claim: any) => void;
  onGoedkeuren: (claim: any, bedrag: number) => void;
  archiveer: (id: string, gearchiveerd: boolean) => void;
}) {
  const btn = 'px-3 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50';
  const [mode, setMode] = useState<'dekking' | 'bedrag' | 'pct'>('dekking');
  const [pct, setPct] = useState('');
  const exBtw = Number(c.factuur_ex_btw || 0);
  const dekking = Number(c.gedekt_bedrag || 0);
  const dekkingspct = Number(c.dekkingspercentage ?? 85);
  // Standaardberekening: uitkering = verzekerd bedrag × dekkingspercentage; de rest is eigen risico
  const berekend = Math.round(dekking * dekkingspct) / 100;
  const eigenRisico = Math.round((dekking - berekend) * 100) / 100;
  const pctAmount = Math.min((exBtw * Number(pct || 0)) / 100, dekking);
  const effAmount = mode === 'dekking' ? berekend : mode === 'pct' ? Math.round(pctAmount * 100) / 100 : Number(amount);
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-3 gap-3 text-xs text-slate-600">
        <div><span className="text-slate-400">Geclaimd bedrag:</span> {fmt(c.claim_amount)}</div>
        <div><span className="text-slate-400">Gedekt (verzekerd):</span> {fmt(c.gedekt_bedrag)}</div>
        <div><span className="text-slate-400">Debiteur:</span> {c.debiteur}</div>
      </div>
      {c.incasso_fee != null && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800 flex items-center justify-between gap-2">
          <span>Incasso-afrekening: geïncasseerd {fmt(c.geincasseerd_bedrag)} → incassokosten <strong>{fmt(c.incasso_fee)}</strong>.</span>
          <button onClick={() => openPdf(`/api/admin/claims/${c.id}/incasso-afrekening`, token)}
            className="font-medium text-mfb-blue hover:underline whitespace-nowrap">📄 Afrekening (PDF)</button>
        </div>
      )}
      {c.invoice_id && <AttachmentsView invoiceId={c.invoice_id} token={token} />}

      {/* INCASSO-spoor — doorgeefluik naar Allianz */}
      {soort === 'incasso' && (
       <div className="space-y-3">
        {/* Volledigheid + doorzetten */}
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Volledigheid voor overdracht naar Allianz</div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
            <Check ok={c.heeft_kopiefactuur} label="Kopiefactuur" />
            <Check ok={c.heeft_leverbewijs} label="Leverbewijs" />
            <Check ok={c.heeft_verkoopvoorwaarden} label="Verkoopvoorwaarden" />
            <Check ok={c.akkoord} label={`Akkoord incassokosten${c.akkoord_bedrag != null ? ` (${fmt(c.akkoord_bedrag)})` : ''}`} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {c.doorgezet_op ? (
              <span className="text-xs text-emerald-700">✓ Doorgezet naar Allianz op {c.doorgezet_op}</span>
            ) : (
              <>
                <button disabled={busy || !c.volledig} title={c.volledig ? '' : 'Dossier is nog niet volledig'}
                  className={`${btn} text-white bg-mfb-blue hover:bg-mfb-blue-dark`}
                  onClick={() => { if (window.confirm('Dit incassodossier doorzetten naar Allianz Trade? De debiteuren- en facturen-export en de stukken worden per e-mail verstuurd.')) act(c.id, 'doorzetten'); }}>
                  Doorzetten naar Allianz
                </button>
                {!c.volledig && <span className="text-xs text-amber-700">Nog niet volledig — het lid moet stukken + akkoord aanleveren.</span>}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {c.status === 'submitted' && (
            <button disabled={busy} className={`${btn} text-mfb-blue border border-slate-300 hover:bg-white`} onClick={() => act(c.id, 'in-behandeling')}>In behandeling nemen</button>
          )}
          {c.status === 'under_review' && (
            <>
              <button disabled={busy} className={`${btn} text-white bg-emerald-600 hover:bg-emerald-700`} onClick={() => {
                const def = String(c.factuur_incl_btw || c.claim_amount || 0);
                const v = window.prompt('Geïncasseerd bedrag (€) — basis voor de afrekening:', def);
                if (v === null) return;
                act(c.id, 'incasso-geslaagd', { geincasseerd: Number(v) || 0 });
              }}>Incasso geslaagd (debiteur betaalde)</button>
              <div className="inline-flex items-center gap-1">
                <select value={reden} onChange={(e) => setReden(e.target.value)} className="w-44 px-2 py-1 border border-slate-300 rounded text-xs">
                  <option value="">— reden mislukt —</option>
                  {['Debiteur onvindbaar', 'Debiteur insolvent', 'Betwiste vordering', 'Geen reactie debiteur', 'Anders'].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button disabled={busy} className={`${btn} text-red-700 border border-red-300 hover:bg-red-50`} onClick={() => act(c.id, 'incasso-mislukt', { reason: reden || 'Niet geïnd' })}>Incasso mislukt</button>
              </div>
            </>
          )}
          {c.status === 'denied' && (
            <button disabled={busy} className={`${btn} text-white bg-mfb-orange hover:bg-mfb-orange-dark`} onClick={() => act(c.id, 'escaleer-naar-schade')}>Escaleer naar schadedossier</button>
          )}
          {['paid', 'withdrawn'].includes(c.status) && <span className="text-xs text-slate-400">Dit incassodossier is afgewikkeld.</span>}
        </div>
       </div>
      )}

      {/* SCHADE-spoor */}
      {soort === 'schade' && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {c.status === 'submitted' && (
              <button disabled={busy} className={`${btn} text-mfb-blue border border-slate-300 hover:bg-white`} onClick={() => act(c.id, 'in-behandeling')}>In beoordeling nemen</button>
            )}
            {(c.status === 'under_review' || c.status === 'disputed') && (
              <div className="w-full space-y-3">
                <div className="rounded-lg border border-slate-200 bg-white p-3 max-w-xl">
                  <div className="flex flex-wrap items-center gap-4 mb-2 text-xs">
                    <span className="text-slate-400">Uitkering vaststellen op:</span>
                    <label className="inline-flex items-center gap-1"><input type="radio" checked={mode === 'dekking'} onChange={() => setMode('dekking')} /> Dekkingspercentage</label>
                    <label className="inline-flex items-center gap-1"><input type="radio" checked={mode === 'bedrag'} onChange={() => setMode('bedrag')} /> Handmatig bedrag</label>
                    <label className="inline-flex items-center gap-1"><input type="radio" checked={mode === 'pct'} onChange={() => setMode('pct')} /> % van factuur</label>
                  </div>
                  {mode === 'dekking' ? (
                    <div className="text-sm text-slate-700 space-y-0.5">
                      <div className="flex justify-between max-w-xs"><span className="text-slate-400">Verzekerd bedrag</span><span className="tabular-nums">{fmt(dekking)}</span></div>
                      <div className="flex justify-between max-w-xs"><span className="text-slate-400">Dekkingspercentage</span><span className="tabular-nums">{dekkingspct}%</span></div>
                      <div className="flex justify-between max-w-xs text-slate-500"><span className="text-slate-400">Eigen risico</span><span className="tabular-nums">{fmt(eigenRisico)}</span></div>
                      <div className="flex justify-between max-w-xs font-semibold border-t border-slate-200 pt-0.5"><span>Uitkering</span><span className="tabular-nums">{fmt(berekend)}</span></div>
                    </div>
                  ) : mode === 'bedrag' ? (
                    <div className="inline-flex items-center gap-1">
                      <span className="text-xs text-slate-400">€</span>
                      <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-32 px-2 py-1 border border-slate-300 rounded text-sm" />
                      <span className="text-xs text-slate-400 ml-1">(max. dekking {fmt(dekking)})</span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 flex-wrap">
                      <div className="inline-flex items-center gap-1">
                        <input type="number" value={pct} onChange={(e) => setPct(e.target.value)} className="w-20 px-2 py-1 border border-slate-300 rounded text-sm" />
                        <span className="text-xs text-slate-400">% van {fmt(exBtw)} (ex btw)</span>
                      </div>
                      <span className="text-sm font-medium text-slate-700">= {fmt(effAmount)}</span>
                      {pctAmount >= dekking && dekking > 0 && <span className="text-[10px] text-mfb-orange">gemaximeerd op dekking</span>}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button disabled={busy || !(effAmount > 0)} className={`${btn} text-white bg-emerald-600 hover:bg-emerald-700`} onClick={() => onGoedkeuren(c, effAmount)}>Verzekerd goedkeuren — uitkeren {fmt(effAmount)}</button>
                  <button disabled={busy} className={`${btn} text-mfb-blue border border-mfb-blue/40 hover:bg-mfb-blue/5`} onClick={() => onBeoordeel(c)}>Beoordelen — afwijzen / betwisten…</button>
                </div>
              </div>
            )}
            {c.status === 'approved' && (
              <button disabled={busy} className={`${btn} text-white bg-mfb-orange hover:bg-mfb-orange-dark`} onClick={() => act(c.id, 'uitkeren')}>Uitkeren</button>
            )}
            {['paid', 'denied'].includes(c.status) && <span className="text-xs text-slate-400">Dit schadedossier is afgehandeld.</span>}
          </div>
          <button onClick={schadestaat} className="text-xs font-medium text-mfb-blue hover:underline">📄 Schadestaat opstellen / openen (PDF)</button>
        </div>
      )}

      <div className="flex justify-end border-t border-slate-200 pt-2">
        {c.gearchiveerd
          ? <button disabled={busy} onClick={() => archiveer(c.id, false)} className="px-2.5 py-1.5 text-xs font-medium border border-slate-300 text-slate-600 hover:bg-white rounded-lg disabled:opacity-50">Terughalen uit archief</button>
          : <button disabled={busy} onClick={() => archiveer(c.id, true)} className="px-2.5 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg disabled:opacity-50">Archiveer dossier</button>}
      </div>
    </div>
  );
}

export default AdminClaimsPage;
